// app/javascript/hooks/useArmyDepartments.js
import { useState, useEffect, useMemo } from 'react';
import { toSomaliDept, toEnglishDept, ENGLISH_TO_SOMALI_DEPT, SOMALI_TO_ENGLISH_DEPT, getAllDeptSynonyms } from '../utils/deptTranslations.js';

// ── Client-approved exact structure (Somali names) ───────────────────────────
export const ARMY_STRUCTURE = {
  'Puntland Military Force': {
    departments: [
      'Taliyayaal',
      'Hogaamo (8 Hogaan)',
      'Taliska Guud',
      'Qeybaha',
      'Colal Madax Banaan',
      'Kooxda Dugsiyadda 30 iyo Naafada',
    ],
    subDepartments: {
      'Taliyayaal': ['Taliye Kuxigeen', 'Abaanduul'],
      'Hogaamo (8 Hogaan)': [
        'Hogaanka Ciidanka',
        'Hogaanka Hawlgalinta',
        'Hogaanka Tababarka',
        'Hogaanka Isgaarsiinta',
        'Hogaanka Gaadiidka iyo Farsamada',
        'Hogaanka Taakulada',
        'Hogaanka Caafimaadka',
        'Hogaanka Maamulka Lacagta',
      ],
      'Taliska Guud': ['Ururka Taliska (Ilaalada, Dhamaan)', 'Ururka Taliska', 'Headquarters', 'Central Headquarters', 'HQ'],
      'Qeybaha': [
        'Qeybta 60aad',
        'Qeybta 7aad Bari',
        'Qeybta 9aad Sanaag',
        'Qeybta 26aad Sool',
        'Aaga Cayn',
      ],
      'Colal Madax Banaan': [
        'Dhimashada',
        'Guutada Dufaan (Nugaal Base)',
        'Guutada Kumaandooska (Nugaal Base)',
        'Ururka 48aad (Qardho)',
        'Hurinta Kowaad ee Taliska (Garowe)',
      ],
      'Kooxda Dugsiyadda 30 iyo Naafada': [],
    },
  },
  'Puntland Police Force': {
    departments: [
      'Taliska Booliska',
      'Laamaha Madaxa Banaan',
      'Qeybaha Gobollada',
    ],
    subDepartments: {
      'Taliska Booliska': [
        'Taliye',
        'Taliye Kuxigeen 1',
        'Taliye Kuxigeen 2',
        'Hogaanka Ciidanka',
        'Hogaanka Maaliyadda',
        'Hogaanka Saadka',
        'Boolisaynta Bulshada',
        'Hogaanka Tababarada',
        'Hogaanka Baarista Dambiyada',
        'Hogaanka Ilaalinta Cashuuraha',
        'Hogaanka Nabadgalyada Wadooyinka',
        'Hogaanka Hawlaha',
      ],
      'Laamaha Madaxa Banaan': [
        'Laanta Socotada',
        'Ururka Birmadka',
        'Ururka SPU',
        'K.Tababarada Carmo',
        'Waaxada Caafimaadka',
        'Waaxda Gaadiidka',
        'Xafiiska Warqabka',
        'Xafiiska Afhayeenka',
      ],
      'Qeybaha Gobollada': [
        'Bari', 'Nugaal', 'Mudug', 'Sanaag',
        'Karkaar', 'Haylaan', 'Raascaseyr', 'Sool', 'Cayn',
      ],
    },
  },
  'Puntland Custodial Corps (Asluubta)': {
    departments: [
      'Taliska Asluubta',
      'Laamaha Madaxa Banaan',
      'Xabsiyada Gobollada',
    ],
    subDepartments: {
      'Taliska Asluubta': [
        'Taliye',
        'Taliye Kuxigeen 1aad',
        'Taliye Kuxigeen 2aad',
        'Hogaanka Ciidanka',
        'Hogaanka Maaliyadda',
        'Hogaanka Caafimaadka',
        'Hogaanka Tababarada iyo Qorsheynta',
        'Hogaanka Baariska iyo Kormeerka Xabsiyada',
        'Hogaanka Siyaasada iyo Wacyigalinta',
      ],
      'Laamaha Madaxa Banaan': [
        'Kuliyada Ciidanka Asluubta',
        'Waaxda Gaadiidka',
        'Waaxda Dhismaha',
        'Waaxda Xafiiska Warqabka',
        'Xafiiska Afhayeenka',
        'Horinta Taliska Guud',
      ],
      'Xabsiyada Gobollada': [
        'Bari', 'Karkaar', 'Nugaal', 'Sanaag', 'Mudug',
      ],
    },
  },
};

// Aliases mapping for flexible force resolution
export const FORCE_ALIASES = {
  'puntland military force': 'Puntland Military Force',
  'puntland darawiish force': 'Puntland Military Force',
  'darawiish force': 'Puntland Military Force',
  'darawiish': 'Puntland Military Force',
  'darawish': 'Puntland Military Force',
  'military force': 'Puntland Military Force',
  'military': 'Puntland Military Force',
  'pmf': 'Puntland Military Force',
  'ciidanka darawiishta': 'Puntland Military Force',
  'ciidamada difaaca puntland': 'Puntland Military Force',

  'puntland police force': 'Puntland Police Force',
  'police force': 'Puntland Police Force',
  'police': 'Puntland Police Force',
  'ppf': 'Puntland Police Force',
  'ciidanka booliska': 'Puntland Police Force',
  'boolis': 'Puntland Police Force',

  'puntland custodial corps (asluubta)': 'Puntland Custodial Corps (Asluubta)',
  'puntland custodial corps': 'Puntland Custodial Corps (Asluubta)',
  'custodial corps': 'Puntland Custodial Corps (Asluubta)',
  'custodial corps (asluubta)': 'Puntland Custodial Corps (Asluubta)',
  'asluubta': 'Puntland Custodial Corps (Asluubta)',
  'discipline': 'Puntland Custodial Corps (Asluubta)',
  'pcc': 'Puntland Custodial Corps (Asluubta)',
  'pcc(': 'Puntland Custodial Corps (Asluubta)',
  'ciidanka asluubta': 'Puntland Custodial Corps (Asluubta)',
};

// Somali name → English name
export const SOMALI_TO_ENGLISH = {
  'Taliyayaal': 'Commanders',
  'Hogaamo (8 Hogaan)': 'Leaders (8 leaders)',
  'Taliska Guud': 'General Command',
  'Qeybaha': 'Parts',
  'Colal Madax Banaan': 'Independent Wars',
  'Kooxda Dugsiyadda 30 iyo Naafada': 'School Team30 and Disability',
  'Taliska Booliska': 'Police Command',
  'Laamaha Madaxa Banaan': 'Independent Branches',
  'Qeybaha Gobollada': 'Regional Divisions',
  'Taliska Asluubta': 'Prisons Command',
  'Xabsiyada Gobollada': 'Regional Prisons',
  ...SOMALI_TO_ENGLISH_DEPT,
};

// English → Somali
export const ENGLISH_TO_SOMALI = {
  ...ENGLISH_TO_SOMALI_DEPT,
  ...Object.fromEntries(
    Object.entries(SOMALI_TO_ENGLISH).map(([s, e]) => [e, s])
  ),
};

export const toSomali = (name) => toSomaliDept(name) || ENGLISH_TO_SOMALI[name] || name;
export const toEnglish = (name) => toEnglishDept(name) || SOMALI_TO_ENGLISH[name] || name;

/**
 * Resolves any force identifier (ID, database name, slug, Somali formal name)
 * to the matching ARMY_STRUCTURE key.
 */
export const getStructureKeyForForce = (nameOrId, armyTypes = []) => {
  if (!nameOrId) return '';
  const str = String(nameOrId).trim().toLowerCase();

  if (FORCE_ALIASES[str]) return FORCE_ALIASES[str];

  // If it's an ID, find the armyType name first
  const found = armyTypes.find(a => String(a.id) === String(nameOrId));
  if (found) {
    const foundStr = String(found.name || '').trim().toLowerCase();
    if (FORCE_ALIASES[foundStr]) return FORCE_ALIASES[foundStr];
    if (foundStr.includes('police') || foundStr.includes('boolis') || foundStr === 'ppf') return 'Puntland Police Force';
    if (foundStr.includes('custodial') || foundStr.includes('asluub') || foundStr.includes('discipline') || foundStr === 'pcc') return 'Puntland Custodial Corps (Asluubta)';
    if (foundStr.includes('military') || foundStr.includes('darawiish') || foundStr.includes('darawish') || foundStr.includes('difaac') || foundStr === 'pmf') return 'Puntland Military Force';
    return found.name;
  }

  // Substring match
  if (str.includes('police') || str.includes('boolis')) return 'Puntland Police Force';
  if (str.includes('custodial') || str.includes('asluub') || str.includes('discipline')) return 'Puntland Custodial Corps (Asluubta)';
  if (str.includes('military') || str.includes('darawiish') || str.includes('darawish') || str.includes('difaac')) return 'Puntland Military Force';

  return String(nameOrId);
};

/**
 * Pure function: computes the list of departments belonging strictly to the selected force.
 */
export const computeDepartments = (armyTypeIdOrName, allDepartments = [], allArmyTypes = []) => {
  if (!armyTypeIdOrName || armyTypeIdOrName === 'all') {
    // If no specific force selected, return all main departments (no parent)
    const seen = new Set();
    return allDepartments
      .filter(d => d && !d.parent_id)
      .map(d => {
        const somaliName = toSomaliDept(d.name);
        return {
          id: d.id,
          name: d.name,
          somali_name: somaliName,
          _somali: somaliName,
          _isStatic: false,
          army_type_id: d.army_type_id,
        };
      })
      .filter(d => {
        const k = (d.somali_name || d.name || '').toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
  }

  const structureKey = getStructureKeyForForce(armyTypeIdOrName, allArmyTypes);
  const armyTypeRecord = allArmyTypes.find(a =>
    String(a.id) === String(armyTypeIdOrName) ||
    a.name === armyTypeIdOrName ||
    getStructureKeyForForce(a.name, allArmyTypes) === structureKey
  );
  const resolvedArmyTypeId = armyTypeRecord?.id;

  const structure = ARMY_STRUCTURE[structureKey];

  // All DB top-level depts for this army type (or unassigned/global)
  const dbDepts = allDepartments.filter(d =>
    d && !d.parent_id &&
    (
      (resolvedArmyTypeId && String(d.army_type_id) === String(resolvedArmyTypeId)) ||
      (structureKey && getStructureKeyForForce(d.army_type, allArmyTypes) === structureKey) ||
      !d.army_type_id
    )
  );

  let result = [];

  if (structure) {
    const usedDbIds = new Set();

    // Step 1: Build from hardcoded client-approved list, match each to a DB record by name
    result = structure.departments.map((somaliName, idx) => {
      const englishName = toEnglish(somaliName) || somaliName;
      const synonyms = getAllDeptSynonyms(somaliName);
      let dbRecord = dbDepts.find(d => {
        const dName = (d.name || "").trim().toLowerCase();
        const dSomali = toSomaliDept(d.name).trim().toLowerCase();
        return (
          dName === somaliName.toLowerCase() ||
          dSomali === somaliName.toLowerCase() ||
          dName === englishName.toLowerCase() ||
          synonyms.includes(dName) ||
          synonyms.includes(dSomali)
        );
      });
      if (!dbRecord) {
        dbRecord = allDepartments.find(d => {
          if (usedDbIds.has(d.id)) return false;
          const dName = (d.name || "").trim().toLowerCase();
          const dSomali = toSomaliDept(d.name).trim().toLowerCase();
          return (
            dName === somaliName.toLowerCase() ||
            dSomali === somaliName.toLowerCase() ||
            dName === englishName.toLowerCase() ||
            synonyms.includes(dName) ||
            synonyms.includes(dSomali)
          );
        });
      }
      if (dbRecord) usedDbIds.add(dbRecord.id);
      return {
        id: dbRecord?.id ?? `static_dept_${idx}_${resolvedArmyTypeId || armyTypeIdOrName}`,
        name: dbRecord?.name ?? englishName,
        somali_name: somaliName,
        _somali: somaliName,
        _isStatic: !dbRecord,
        army_type_id: dbRecord?.army_type_id || resolvedArmyTypeId,
      };
    });

    // Step 2: Append DB extras not covered by hardcoded list (new ones from Army Config)
    const hardcodedSomaliSet = new Set(structure.departments.map(n => n.toLowerCase()));
    const hardcodedEnglishSet = new Set(structure.departments.map(n => (toEnglish(n) || n).toLowerCase()));
    const seenNames = new Set(result.map(r => r.somali_name.toLowerCase()));

    dbDepts
      .filter(d => !usedDbIds.has(d.id))
      .forEach(d => {
        const displayName = toSomaliDept(d.name);
        const nameLower = (d.name || '').toLowerCase();
        const displayLower = displayName.toLowerCase();
        if (
          hardcodedSomaliSet.has(nameLower) ||
          hardcodedSomaliSet.has(displayLower) ||
          hardcodedEnglishSet.has(nameLower) ||
          hardcodedEnglishSet.has(displayLower)
        ) return;
        if (!seenNames.has(displayLower)) {
          seenNames.add(displayLower);
          result.push({
            id: d.id,
            name: d.name,
            somali_name: displayName,
            _somali: displayName,
            _isStatic: false,
            army_type_id: d.army_type_id || resolvedArmyTypeId,
          });
        }
      });
  } else {
    // Unknown or custom army type — fall back entirely to DB
    const seen = new Set();
    result = dbDepts
      .filter(d => {
        const k = toSomaliDept(d.name).toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .map(d => ({
        id: d.id,
        name: d.name,
        somali_name: toSomaliDept(d.name),
        _somali: toSomaliDept(d.name),
        _isStatic: false,
        army_type_id: d.army_type_id,
      }));
  }

  return result;
};

/**
 * Pure function: computes the list of sub-departments belonging strictly to the selected force & department.
 */
export const computeSubDepartments = (
  departmentIdOrName,
  armyTypeIdOrName,
  allDepartments = [],
  allArmyTypes = [],
  computedDepts = []
) => {
  if (!departmentIdOrName) return [];

  const structureKey = getStructureKeyForForce(armyTypeIdOrName, allArmyTypes);
  const structure = ARMY_STRUCTURE[structureKey];

  // Find the selected department object
  const idStr = String(departmentIdOrName).trim().toLowerCase();
  const selectedDept =
    computedDepts.find(d =>
      String(d.id).toLowerCase() === idStr ||
      (d.name && d.name.toLowerCase() === idStr) ||
      (d.somali_name && d.somali_name.toLowerCase() === idStr) ||
      (d._somali && d._somali.toLowerCase() === idStr)
    ) ||
    allDepartments.find(d =>
      String(d.id).toLowerCase() === idStr ||
      (d.name && d.name.toLowerCase() === idStr) ||
      toSomaliDept(d.name).toLowerCase() === idStr
    );

  const somaliDeptName =
    selectedDept?._somali ||
    selectedDept?.somali_name ||
    toSomaliDept(selectedDept?.name || departmentIdOrName);

  const englishDeptName =
    toEnglish(somaliDeptName) ||
    selectedDept?.name ||
    departmentIdOrName;

  // Find all DB parent IDs matching this department
  const parentIds = allDepartments
    .filter(d =>
      !d.parent_id &&
      (
        String(d.id) === String(selectedDept?.id) ||
        (d.name && d.name.toLowerCase() === somaliDeptName.toLowerCase()) ||
        (d.name && d.name.toLowerCase() === englishDeptName.toLowerCase()) ||
        (d.name && toSomaliDept(d.name).toLowerCase() === somaliDeptName.toLowerCase())
      )
    )
    .map(d => d.id);

  if (selectedDept?.id && !String(selectedDept.id).startsWith('static_') && !parentIds.includes(selectedDept.id)) {
    parentIds.push(selectedDept.id);
  }

  const dbSubs = allDepartments.filter(d => parentIds.includes(d.parent_id));

  let result = [];

  // Match structure subdepartments case-insensitively
  let hardcodedList = null;
  if (structure?.subDepartments) {
    const matchedKey = Object.keys(structure.subDepartments).find(
      k => k.trim().toLowerCase() === somaliDeptName.trim().toLowerCase()
    );
    if (matchedKey) {
      hardcodedList = structure.subDepartments[matchedKey];
    }
  }

  if (hardcodedList) {
    const usedDbIds = new Set();

    result = hardcodedList.map((somaliName, idx) => {
      const englishName = toEnglish(somaliName) || somaliName;
      const synonyms = getAllDeptSynonyms(somaliName);
      let dbRecord = dbSubs.find(d => {
        const dName = (d.name || "").trim().toLowerCase();
        const dSomali = toSomaliDept(d.name).trim().toLowerCase();
        return (
          dName === somaliName.toLowerCase() ||
          dSomali === somaliName.toLowerCase() ||
          dName === englishName.toLowerCase() ||
          synonyms.includes(dName) ||
          synonyms.includes(dSomali)
        );
      });
      if (!dbRecord) {
        dbRecord = allDepartments.find(d => {
          if (usedDbIds.has(d.id)) return false;
          const dName = (d.name || "").trim().toLowerCase();
          const dSomali = toSomaliDept(d.name).trim().toLowerCase();
          return (
            dName === somaliName.toLowerCase() ||
            dSomali === somaliName.toLowerCase() ||
            dName === englishName.toLowerCase() ||
            synonyms.includes(dName) ||
            synonyms.includes(dSomali)
          );
        });
      }
      if (dbRecord) usedDbIds.add(dbRecord.id);
      return {
        id: dbRecord?.id ?? `static_sub_${somaliName}_${idx}`,
        name: dbRecord?.name ?? englishName,
        somali_name: somaliName,
        _somali: somaliName,
        _isStatic: !dbRecord,
        parent_id: dbRecord?.parent_id || selectedDept?.id,
      };
    });

    const hardcodedSomaliSet = new Set(hardcodedList.map(n => n.toLowerCase()));
    const hardcodedEnglishSet = new Set(hardcodedList.map(n => (toEnglish(n) || n).toLowerCase()));
    const seenNames = new Set(result.map(r => r.somali_name.toLowerCase()));

    dbSubs
      .filter(d => !usedDbIds.has(d.id))
      .forEach(d => {
        const displayName = toSomaliDept(d.name);
        const nameLower = (d.name || '').toLowerCase();
        const displayLower = displayName.toLowerCase();
        if (
          hardcodedSomaliSet.has(nameLower) ||
          hardcodedSomaliSet.has(displayLower) ||
          hardcodedEnglishSet.has(nameLower) ||
          hardcodedEnglishSet.has(displayLower)
        ) return;
        if (!seenNames.has(displayLower)) {
          seenNames.add(displayLower);
          result.push({
            id: d.id,
            name: d.name,
            somali_name: displayName,
            _somali: displayName,
            _isStatic: false,
            parent_id: d.parent_id,
          });
        }
      });
  } else {
    // No hardcoded structure — fall back entirely to DB
    const seen = new Set();
    result = dbSubs
      .filter(d => {
        const k = toSomaliDept(d.name).toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .map(d => ({
        id: d.id,
        name: d.name,
        somali_name: toSomaliDept(d.name),
        _somali: toSomaliDept(d.name),
        _isStatic: false,
        parent_id: d.parent_id,
      }));
  }

  return result;
};

/**
 * React hook for dynamic force-based department and sub-department filtering.
 */
export const useArmyDepartments = (api, initialArmyType = '', initialDepartment = '') => {
  const [armyTypes, setArmyTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedArmyType, setSelectedArmyType] = useState(initialArmyType || '');
  const [selectedDepartment, setSelectedDepartment] = useState(initialDepartment || '');

  // Keep internal state reactive to initial parameter changes
  useEffect(() => {
    if (initialArmyType !== undefined && initialArmyType !== null) {
      setSelectedArmyType(initialArmyType);
    }
  }, [initialArmyType]);

  useEffect(() => {
    if (initialDepartment !== undefined && initialDepartment !== null) {
      setSelectedDepartment(initialDepartment);
    }
  }, [initialDepartment]);

  useEffect(() => {
    if (api) fetchData();
  }, [api]);

  const fetchData = async () => {
    try {
      const [armyTypesData, departmentsData] = await Promise.all([
        api.get('/army_types'),
        api.get('/departments'),
      ]);
      const atList = Array.isArray(armyTypesData) ? armyTypesData : armyTypesData?.army_types || armyTypesData?.data || [];
      const deptList = Array.isArray(departmentsData) ? departmentsData : departmentsData?.departments || departmentsData?.data || [];
      setArmyTypes(atList);
      setDepartments(deptList);
    } catch (err) {
      console.error('Failed to fetch army departments data:', err);
    }
  };

  const filteredDepartments = useMemo(() => {
    return computeDepartments(selectedArmyType, departments, armyTypes);
  }, [selectedArmyType, departments, armyTypes]);

  const filteredSubDepartments = useMemo(() => {
    return computeSubDepartments(selectedDepartment, selectedArmyType, departments, armyTypes, filteredDepartments);
  }, [selectedDepartment, selectedArmyType, departments, armyTypes, filteredDepartments]);

  const handleArmyTypeChange = (armyTypeId) => {
    setSelectedArmyType(armyTypeId);
    setSelectedDepartment('');
  };

  const handleDepartmentChange = (deptId, armyTypeIdOverride) => {
    if (armyTypeIdOverride) {
      setSelectedArmyType(armyTypeIdOverride);
    }
    setSelectedDepartment(deptId);
  };

  return {
    armyTypes,
    departments,
    filteredDepartments,
    filteredSubDepartments,
    selectedArmyType,
    selectedDepartment,
    setSelectedArmyType,
    setSelectedDepartment,
    handleArmyTypeChange,
    handleDepartmentChange,
    toSomali: toSomaliDept,
  };
};
